import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const INVOICE_PAGE_WIDTH = 794;

const toInvoiceHtml = (payload) => {
    if (typeof payload === 'string') return payload;
    if (typeof payload?.data === 'string') return payload.data;
    if (typeof payload?.html === 'string') return payload.html;
    if (typeof payload?.data?.html === 'string') return payload.data.html;
    return '';
};

const buildInvoiceSrcDoc = (html) => {
    const extraHead = `
        <meta charset="utf-8" />
        <style>
            html, body { margin: 0; padding: 0; background: #fff; }
        </style>
    `;

    if (/<!DOCTYPE/i.test(html) || /<html[\s>]/i.test(html)) {
        if (/<head[^>]*>/i.test(html)) {
            return html.replace(/<head[^>]*>/i, (match) => `${match}${extraHead}`);
        }
        return html.replace(/<html[^>]*>/i, (match) => `${match}<head>${extraHead}</head>`);
    }

    return `<!DOCTYPE html><html><head>${extraHead}</head><body>${html}</body></html>`;
};

const InvoicePreviewModal = ({ htmlContent, onClose, title = 'Invoice Preview' }) => {
    const iframeRef = useRef(null);
    const wrapRef = useRef(null);
    const html = toInvoiceHtml(htmlContent);
    const srcDoc = useMemo(() => buildInvoiceSrcDoc(html), [html]);
    const [docHeight, setDocHeight] = useState(1100);
    const [docWidth, setDocWidth] = useState(INVOICE_PAGE_WIDTH);
    const [scale, setScale] = useState(() => (
        typeof window === 'undefined'
            ? 1
            : Math.min(1, (window.innerWidth - 24) / INVOICE_PAGE_WIDTH)
    ));

    const measure = () => {
        const iframe = iframeRef.current;
        const wrap = wrapRef.current;
        if (!iframe || !wrap) return;
        const doc = iframe.contentDocument;
        if (!doc?.documentElement) return;

        const nextHeight = Math.max(
            doc.body?.scrollHeight || 0,
            doc.documentElement.scrollHeight || 0,
            480
        );
        const nextWidth = Math.max(
            doc.body?.scrollWidth || 0,
            doc.documentElement.scrollWidth || 0,
            INVOICE_PAGE_WIDTH
        );
        const nextScale = Math.min(1, wrap.clientWidth / nextWidth);

        setDocHeight((current) => (Math.abs(current - nextHeight) > 2 ? nextHeight : current));
        setDocWidth((current) => (Math.abs(current - nextWidth) > 2 ? nextWidth : current));
        setScale((current) => (Math.abs(current - nextScale) > 0.01 ? nextScale : current));
    };

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return undefined;

        let observer;
        const handleLoad = () => {
            measure();
            const doc = iframe.contentDocument;
            if (!doc?.body || typeof ResizeObserver === 'undefined') return;
            observer = new ResizeObserver(() => measure());
            observer.observe(doc.body);
        };

        iframe.addEventListener('load', handleLoad);
        window.addEventListener('resize', measure);
        return () => {
            iframe.removeEventListener('load', handleLoad);
            window.removeEventListener('resize', measure);
            observer?.disconnect();
        };
    }, [srcDoc]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [onClose]);

    if (!html) return null;

    return createPortal(
        <div className="fixed inset-0 z-[10000] flex flex-col bg-slate-900/60">
            <div className="flex h-full min-h-0 w-full flex-col bg-white">
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[#0e2c4a] bg-[#12355B] px-4 py-3 text-white">
                    <h2 className="text-base font-bold sm:text-lg">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md p-1 text-white hover:bg-white/10"
                        aria-label="Close invoice preview"
                    >
                        <X size={22} />
                    </button>
                </div>

                <div ref={wrapRef} className="min-h-0 flex-1 overflow-auto bg-slate-200 px-2 py-4 sm:px-6 sm:py-6">
                    <div
                        className="mx-auto overflow-hidden bg-white shadow-xl"
                        style={{
                            width: Math.max(1, Math.floor(docWidth * scale)),
                            height: Math.max(1, Math.floor(docHeight * scale))
                        }}
                    >
                        <iframe
                            ref={iframeRef}
                            title={title}
                            srcDoc={srcDoc}
                            sandbox="allow-same-origin"
                            onLoad={() => {
                                measure();
                                window.setTimeout(measure, 250);
                            }}
                            className="block border-0 bg-white"
                            style={{
                                width: docWidth,
                                height: docHeight,
                                transform: `scale(${scale})`,
                                transformOrigin: 'top left'
                            }}
                        />
                    </div>
                </div>

                <div className="flex shrink-0 justify-end border-t bg-white px-4 py-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 sm:w-auto"
                    >
                        Close Preview
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default InvoicePreviewModal;
